// /planes — gestión de planes (presets de capabilities).
//
// Solo superadmin. Los planes definen qué capabilities trae cada preset.
// Editar capabilities del plan NO afecta retroactivamente a tenants ya
// asignados (hay que re-aplicar el plan en /clientes). Renombrar el slug SÍ
// actualiza tenants.plan de los clientes que tenían el slug viejo.
//
// El gating del CRM lee tenant.capabilities, NO el slug del plan.

import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent } from 'react';
import {
  Plus,
  Edit3,
  Archive,
  ArchiveRestore,
  ShieldAlert,
  X,
} from 'lucide-react';
import { currentUser } from '~/lib/pb';
import {
  usePlans,
  useCreatePlan,
  useUpdatePlan,
  useArchivePlan,
  type Plan,
} from '~/lib/plans-queries';
import { useTenants } from '~/lib/tenants-queries';
import {
  CAPABILITY_KEYS,
  CAPABILITY_LABELS,
  CUSTOM_PLAN_SLUG,
  type Capability,
} from '~/lib/capabilities';
import { Tooltip } from '~/components/Tooltip/Tooltip';
import { useConfirm } from '~/components/ConfirmDialog/ConfirmDialog';
import styles from './_authed.clientes.module.css';

function slugify(input: string): string {
  return input
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

export const Route = createFileRoute('/_authed/planes')({
  component: PlanesPage,
});

function PlanesPage() {
  const me = currentUser();
  const isSuperadmin = me?.role === 'superadmin';
  const plansQ = usePlans();
  const tenantsQ = useTenants();
  const [editTarget, setEditTarget] = useState<Plan | null>(null);
  const [creating, setCreating] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  if (!isSuperadmin) {
    return (
      <div className={styles.page}>
        <div className={styles.notSuperadmin}>
          <ShieldAlert size={32} aria-hidden style={{ marginBottom: 12 }} />
          <p>La gestión de planes es solo para superadmins.</p>
        </div>
      </div>
    );
  }

  const tenantCount = new Map<string, number>();
  for (const t of tenantsQ.data ?? []) {
    tenantCount.set(t.plan, (tenantCount.get(t.plan) ?? 0) + 1);
  }

  const all = plansQ.data ?? [];
  const archivedCount = all.filter((p) => p.archived).length;
  const visible = showArchived ? all : all.filter((p) => !p.archived);

  return (
    <div className={styles.page}>
      <header className="page-header">
        <div className="page-header__titles">
          <h1 className="h2">Planes</h1>
        </div>
        <div className="page-header__actions">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setCreating(true)}
          >
            <Plus size={16} aria-hidden />
            Crear plan
          </button>
        </div>
      </header>

      {plansQ.isLoading && <div className="empty-state">Cargando planes…</div>}

      {!plansQ.isLoading && visible.length === 0 && (
        <div className="empty-state">
          No hay planes todavía. Crea el primero con el botón arriba.
        </div>
      )}

      {visible.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Plan</th>
              <th>Clientes</th>
              <th>Capacidades</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visible.map((p) => {
              const count = tenantCount.get(p.slug) ?? 0;
              const capsLabel =
                p.capabilities.length === 0
                  ? '—'
                  : p.capabilities
                      .map((c) => CAPABILITY_LABELS[c]?.replace(/\s*\(.*\)$/, '') ?? c)
                      .join(' · ');
              return (
                <tr key={p.id}>
                  <td>
                    <Tooltip content={<>slug: <code>{p.slug}</code></>}>
                      <span
                        className={`${styles.badge} ${styles[`badge__${p.slug}` as keyof typeof styles] ?? ''}`}
                      >
                        {p.label}
                      </span>
                    </Tooltip>
                    {p.archived && (
                      <span
                        className={styles.statusInactive}
                        style={{ marginLeft: 8 }}
                      >
                        archivado
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={styles.statusInactive}>
                      {count} {count === 1 ? 'cliente' : 'clientes'}
                    </span>
                  </td>
                  <td>
                    <Tooltip content={<div className={styles.planTip}>{capsLabel}</div>}>
                      <span
                        style={{
                          fontSize: 'var(--font-size-meta)',
                          color: 'var(--color-text-subtle)',
                          display: 'inline-block',
                          maxWidth: '32ch',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          verticalAlign: 'bottom',
                        }}
                      >
                        {capsLabel}
                      </span>
                    </Tooltip>
                  </td>
                  <td>
                    <div className={styles.rowActions}>
                      <PlanActions
                        plan={p}
                        tenantCount={count}
                        onEdit={() => setEditTarget(p)}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {archivedCount > 0 && (
        <div style={{ marginTop: 4 }}>
          <button
            type="button"
            className={styles.buttonGhost}
            onClick={() => setShowArchived((v) => !v)}
          >
            {showArchived
              ? 'Ocultar archivados'
              : `Mostrar archivados (${archivedCount})`}
          </button>
        </div>
      )}

      {editTarget && (
        <PlanEditModal
          plan={editTarget}
          tenantCount={tenantCount.get(editTarget.slug) ?? 0}
          onClose={() => setEditTarget(null)}
        />
      )}
      {creating && <PlanCreateModal onClose={() => setCreating(false)} />}
    </div>
  );
}

function PlanActions({
  plan,
  tenantCount,
  onEdit,
}: {
  plan: Plan;
  tenantCount: number;
  onEdit: () => void;
}) {
  const archive = useArchivePlan();
  const confirm = useConfirm();
  const isArchived = plan.archived;
  const blockArchive = !isArchived && tenantCount > 0;
  return (
    <>
      <button
        type="button"
        className={styles.iconButton}
        onClick={onEdit}
        title="Editar"
        aria-label={`Editar ${plan.label}`}
      >
        <Edit3 size={14} aria-hidden />
      </button>
      <button
        type="button"
        className={styles.iconButton}
        disabled={blockArchive}
        title={
          blockArchive
            ? 'No puedes archivar un plan con clientes asignados'
            : isArchived
              ? 'Restaurar'
              : 'Archivar'
        }
        aria-label={
          isArchived ? `Restaurar ${plan.label}` : `Archivar ${plan.label}`
        }
        onClick={async () => {
          const ok = await confirm({
            title: isArchived ? 'Restaurar plan' : 'Archivar plan',
            description: isArchived
              ? `Restaurar "${plan.label}" lo vuelve a mostrar como opción al crear/editar clientes.`
              : `Archivar "${plan.label}" lo oculta del selector al crear/editar clientes. No afecta a clientes ya asignados.`,
            confirmLabel: isArchived ? 'Restaurar' : 'Archivar',
            tone: isArchived ? 'primary' : 'warning',
          });
          if (!ok) return;
          await archive.mutateAsync({ id: plan.id, archived: !isArchived });
        }}
      >
        {isArchived ? (
          <ArchiveRestore size={14} aria-hidden />
        ) : (
          <Archive size={14} aria-hidden />
        )}
      </button>
    </>
  );
}

// ── Modal de crear plan ──────────────────────────────────────────────

function PlanCreateModal({ onClose }: { onClose: () => void }) {
  const create = useCreatePlan();
  const [slug, setSlug] = useState('');
  const [label, setLabel] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [position, setPosition] = useState<number>(100);
  const [capabilities, setCapabilities] = useState<Set<Capability>>(new Set());

  function toggle(c: Capability) {
    const next = new Set(capabilities);
    if (next.has(c)) next.delete(c);
    else next.add(c);
    setCapabilities(next);
  }

  function handleLabel(v: string) {
    setLabel(v);
    if (!slugTouched) setSlug(slugify(v));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    const s = slug.trim();
    if (!s || s === CUSTOM_PLAN_SLUG) return;
    try {
      await create.mutateAsync({
        slug: s,
        label: label.trim(),
        position,
        capabilities: Array.from(capabilities),
      });
      onClose();
    } catch {
      /* mostrado abajo */
    }
  }

  return (
    <PlanModalShell title="Crear plan" onClose={onClose}>
      <form onSubmit={submit} style={{ display: 'contents' }}>
        <div className={styles.modal__body}>
          <PlanFields
            slug={slug}
            label={label}
            position={position}
            capabilities={capabilities}
            onSlug={(v) => {
              setSlugTouched(true);
              setSlug(slugify(v));
            }}
            onLabel={handleLabel}
            onPosition={setPosition}
            onToggle={toggle}
          />
          {create.error && (
            <p className={styles.error}>{String((create.error as Error).message)}</p>
          )}
        </div>
        <div className={styles.modal__footer}>
          <div className={styles.modal__footerLeft} />
          <div className={styles.modal__footerRight}>
            <button type="button" className={styles.buttonGhost} onClick={onClose}>
              Cancelar
            </button>
            <button
              type="submit"
              className={styles.button}
              disabled={
                create.isPending || !slug || !label || slug === CUSTOM_PLAN_SLUG
              }
            >
              {create.isPending ? 'Creando…' : 'Crear plan'}
            </button>
          </div>
        </div>
      </form>
    </PlanModalShell>
  );
}

// ── Modal de editar plan ─────────────────────────────────────────────

function PlanEditModal({
  plan,
  tenantCount,
  onClose,
}: {
  plan: Plan;
  tenantCount: number;
  onClose: () => void;
}) {
  const update = useUpdatePlan();
  const confirm = useConfirm();
  const [slug, setSlug] = useState(plan.slug);
  const [label, setLabel] = useState(plan.label);
  const [slugTouched, setSlugTouched] = useState(false);
  const [position, setPosition] = useState<number>(plan.position);
  const [capabilities, setCapabilities] = useState<Set<Capability>>(
    () => new Set(plan.capabilities),
  );

  useEffect(() => {
    setSlug(plan.slug);
    setLabel(plan.label);
    setPosition(plan.position);
    setCapabilities(new Set(plan.capabilities));
    setSlugTouched(false);
  }, [plan.id, plan.slug, plan.label, plan.position, plan.capabilities]);

  function toggle(c: Capability) {
    const next = new Set(capabilities);
    if (next.has(c)) next.delete(c);
    else next.add(c);
    setCapabilities(next);
  }

  function handleLabel(v: string) {
    setLabel(v);
    if (!slugTouched) setSlug(slugify(v));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    const nextSlug = slug.trim();
    if (!nextSlug || nextSlug === CUSTOM_PLAN_SLUG) return;
    const slugChanged = nextSlug !== plan.slug;
    if (slugChanged && tenantCount > 0) {
      const ok = await confirm({
        title: 'Renombrar slug del plan',
        description: `Vas a cambiar "${plan.slug}" → "${nextSlug}". Se actualizará en ${tenantCount} cliente${tenantCount === 1 ? '' : 's'} que usan este plan. El acceso (capabilities) no cambia.`,
        confirmLabel: 'Renombrar y guardar',
        tone: 'warning',
      });
      if (!ok) return;
    }
    try {
      await update.mutateAsync({
        id: plan.id,
        previousSlug: plan.slug,
        slug: nextSlug,
        label: label.trim(),
        position,
        capabilities: Array.from(capabilities),
      });
      onClose();
    } catch {
      /* mostrado abajo */
    }
  }

  return (
    <PlanModalShell title="Editar plan" onClose={onClose}>
      <form onSubmit={submit} style={{ display: 'contents' }}>
        <div className={styles.modal__body}>
          <PlanFields
            slug={slug}
            label={label}
            position={position}
            capabilities={capabilities}
            onSlug={(v) => {
              setSlugTouched(true);
              setSlug(slugify(v));
            }}
            onLabel={handleLabel}
            onPosition={setPosition}
            onToggle={toggle}
          />
          {update.error && (
            <p className={styles.error}>{String((update.error as Error).message)}</p>
          )}
        </div>
        <div className={styles.modal__footer}>
          <div className={styles.modal__footerLeft} />
          <div className={styles.modal__footerRight}>
            <button type="button" className={styles.buttonGhost} onClick={onClose}>
              Cancelar
            </button>
            <button
              type="submit"
              className={styles.button}
              disabled={
                update.isPending || !label || !slug || slug === CUSTOM_PLAN_SLUG
              }
            >
              {update.isPending ? 'Guardando…' : 'Guardar cambios'}
            </button>
          </div>
        </div>
      </form>
    </PlanModalShell>
  );
}

function PlanModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.modal__header}>
          <h2>{title}</h2>
          <button
            type="button"
            className={styles.iconButton}
            onClick={onClose}
            aria-label="Cerrar"
          >
            <X size={16} aria-hidden />
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

// ── Fields compartidos entre crear y editar ─────────────────────────

function PlanFields({
  slug,
  label,
  position,
  capabilities,
  onSlug,
  onLabel,
  onPosition,
  onToggle,
}: {
  slug: string;
  label: string;
  position: number;
  capabilities: Set<Capability>;
  onSlug: (v: string) => void;
  onLabel: (v: string) => void;
  onPosition: (v: number) => void;
  onToggle: (c: Capability) => void;
}) {
  return (
    <>
      <div className={styles.field}>
        <label className={styles.field__label}>Nombre visible</label>
        <input
          type="text"
          className={styles.input}
          value={label}
          onChange={(e) => onLabel(e.target.value)}
          placeholder="ej: Focus Completo + Ads"
          required
          autoFocus
        />
      </div>

      <div className={styles.field}>
        <label className={styles.field__label}>Slug</label>
        <input
          type="text"
          className={styles.input}
          value={slug}
          onChange={(e) => onSlug(e.target.value)}
          pattern="^[a-z0-9][a-z0-9-]*$"
          placeholder="ej: focus-completo-ads"
          required
        />
        <small className={styles.field__hint}>
          Identificador interno (minúsculas y guiones). Se sugiere desde el nombre;
          si lo cambias, se actualiza en los clientes que usan este plan. No afecta
          el acceso — eso lo definen las capacidades.
        </small>
      </div>

      <div className={styles.field}>
        <label className={styles.field__label}>Orden</label>
        <input
          type="number"
          className={styles.input}
          value={position}
          onChange={(e) => onPosition(Number(e.target.value))}
          min={0}
          step={1}
          style={{ maxWidth: 120 }}
        />
        <small className={styles.field__hint}>Menor = aparece primero en la lista.</small>
      </div>

      <div className={styles.field}>
        <label className={styles.field__label}>Capacidades incluidas</label>
        <div className={styles.capGrid}>
          {CAPABILITY_KEYS.map((c) => (
            <label key={c} className={styles.capToggle}>
              <input
                type="checkbox"
                checked={capabilities.has(c)}
                onChange={() => onToggle(c)}
              />
              <span>{CAPABILITY_LABELS[c]}</span>
            </label>
          ))}
        </div>
      </div>
    </>
  );
}
